import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * Generic Entity Modal Form.
 *
 * Props:
 *  - open / onOpenChange
 *  - title, description
 *  - fields: [{ name, label, type, required, options?, placeholder?, hidden? }]
 *      type: 'text' | 'date' | 'number' | 'textarea' | 'select'
 *  - initialValues: object (for edit)
 *  - onSubmit: async (values) => void (parent closes modal on success)
 *  - submitLabel
 */
const EntityFormModal = ({
  open,
  onOpenChange,
  title,
  description,
  fields = [],
  initialValues = {},
  onSubmit,
  submitLabel = 'Salva',
}) => {
  const [values, setValues] = useState(initialValues || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(initialValues || {});
  }, [initialValues, open]);

  const setField = (name, val) => setValues((p) => ({ ...p, [name]: val }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    // Validate required
    for (const f of fields) {
      if (f.required && !values[f.name]) {
        toast.error(`Il campo "${f.label}" è obbligatorio`);
        return;
      }
    }
    setSaving(true);
    try {
      await onSubmit(values);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.filter((f) => !f.hidden).map((f) => {
              const id = `f-${f.name}`;
              const v = values[f.name] ?? '';
              const col = f.colSpan === 2 ? 'md:col-span-2' : '';
              return (
                <div key={f.name} className={col}>
                  <Label htmlFor={id} className="text-xs font-semibold text-slate-700">
                    {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  <div className="mt-1.5">
                    {f.type === 'textarea' ? (
                      <Textarea
                        id={id}
                        value={v}
                        onChange={(e) => setField(f.name, e.target.value)}
                        placeholder={f.placeholder || ''}
                        rows={f.rows || 3}
                        className="resize-none"
                      />
                    ) : f.type === 'checkbox' ? (
                      <label className="flex items-center gap-2 cursor-pointer py-2">
                        <input
                          type="checkbox"
                          id={id}
                          checked={!!v}
                          onChange={(e) => setField(f.name, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-[#023F80]"
                        />
                        <span className="text-sm text-slate-700">{f.checkboxLabel || 'Sì'}</span>
                      </label>
                    ) : f.type === 'multiselect' ? (
                      <div className="flex flex-wrap gap-1.5" data-testid={`multiselect-${f.name}`}>
                        {(f.options || []).map((o) => {
                          const arr = Array.isArray(v) ? v : (v ? String(v).split(',').map((s) => s.trim()).filter(Boolean) : []);
                          const active = arr.includes(o.value);
                          return (
                            <button
                              type="button"
                              key={o.value}
                              onClick={() => {
                                const next = active ? arr.filter((x) => x !== o.value) : [...arr, o.value];
                                setField(f.name, next);
                              }}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 ${active ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                            >{o.label}</button>
                          );
                        })}
                      </div>
                    ) : f.type === 'select' ? (
                      <Select value={String(v || '')} onValueChange={(val) => setField(f.name, val)}>
                        <SelectTrigger id={id} className="h-10">
                          <SelectValue placeholder={f.placeholder || 'Seleziona...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(f.options || []).filter((o) => o.value !== undefined && o.value !== '').map((o) => (
                            <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={id}
                        type={f.type || 'text'}
                        value={v}
                        onChange={(e) => setField(f.name, e.target.value)}
                        placeholder={f.placeholder || ''}
                        className="h-10"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="pt-3 flex-row justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-[#023F80] hover:bg-[#012C5B] text-white text-sm font-bold tracking-wider flex items-center gap-2 disabled:opacity-70"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {submitLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EntityFormModal;
